<template>
  <v-dialog
    ref="dialog"
    v-model="modal"
    :return-value.sync="data"
    persistent
    max-width="900px"
    transition="dialog-bottom-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-model="dataFormatada"
        persistent-hint
        :disabled="disabled"
        :label="label"
        readonly
        v-bind="attrs"
        v-on="on"
        dense
        solo
        :error-messages="errorMessages"
        required
        prepend-inner-icon="mdi-calendar"
        hide-details="auto"
        class="date-input"
      >
        <template v-slot:append>
          <v-btn
            v-if="dataFormatada && !disabled"
            icon
            small
            @click.stop="limpar"
            class="clear-btn"
          >
            <v-icon small>mdi-close-circle</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </template>

    <v-card class="date-picker-dialog" elevation="8">
      <v-toolbar color="primary" dark flat>
        <v-icon left>mdi-calendar-range</v-icon>
        <v-toolbar-title>Selecionar Data</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="modal = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-container fluid class="calendar-container pa-4">
        <v-row>
          <!-- Calendário -->
          <v-col cols="12" md="7" class="pr-md-2">
            <v-card class="calendar-card" elevation="0" outlined rounded="lg">
              <div class="calendar-wrapper">
                <v-date-picker
                  v-model="data"
                  full-width
                  scrollable
                  :min="inicioPeriodoEtapa"
                  :max="fimPeriodoEtapa"
                  :allowed-dates="allowedDates"
                  :events="todosEventos.map((e) => e.data)"
                  :event-color="getBadgeColor"
                  @update:picker-date="atualizarMesVisivel"
                  color="primary"
                  header-color="primary"
                  locale="pt-BR"
                  no-title
                  class="elevation-0"
                >
                  <template v-slot:day="{ date, day }">
                    <v-badge v-if="hasEvent(date)" :color="getBadgeColor(date)" dot overlap bottom>
                      <span :class="getDayClasses(date)">{{ day }}</span>
                    </v-badge>
                    <span v-else :class="getDayClasses(date)">{{ day }}</span>
                  </template>
                </v-date-picker>
              </div>
            </v-card>

            <!-- Legenda -->
            <v-card class="mt-3 legend-card" elevation="0" outlined rounded="lg">
              <v-card-text class="pa-3">
                <div class="legend-title mb-2">
                  <v-icon small class="mr-1">mdi-information-outline</v-icon>
                  <span class="text-caption font-weight-medium">Legenda (clique para filtrar)</span>
                </div>
                <v-chip-group column class="legend-chips">
                  <v-chip
                    x-small
                    label
                    :color="filtrosDiaLetivo.letivo ? 'green' : 'grey'"
                    :outlined="!filtrosDiaLetivo.letivo"
                    :dark="filtrosDiaLetivo.letivo"
                    @click="toggleFiltro('letivo')"
                    class="ma-1 cursor-pointer"
                  >
                    <v-icon x-small left>mdi-check-circle</v-icon>
                    Dia Letivo
                  </v-chip>
                  <v-chip
                    x-small
                    label
                    :color="filtrosDiaLetivo.naoLetivo ? 'red' : 'grey'"
                    :outlined="!filtrosDiaLetivo.naoLetivo"
                    :dark="filtrosDiaLetivo.naoLetivo"
                    @click="toggleFiltro('naoLetivo')"
                    class="ma-1 cursor-pointer"
                  >
                    <v-icon x-small left>mdi-close-circle</v-icon>
                    Dia não letivo
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Lista de eventos -->
          <v-col cols="12" md="5" class="pl-md-2">
            <v-card class="events-card" elevation="0" outlined rounded="lg">
              <v-card-title class="events-header pa-3">
                <v-icon left color="primary" size="20">mdi-calendar-month</v-icon>
                <span class="text-subtitle-1 font-weight-medium">Eventos do Mês</span>
              </v-card-title>

              <v-divider></v-divider>

              <div class="events-list-container">
                <v-list dense class="py-0 transparent" v-if="eventosFiltradosPorMes.length > 0">
                  <template v-for="(evento, index) in eventosFiltradosPorMes">
                    <v-list-item class="event-item" :key="`evento-${index}`">
                      <v-list-item-avatar size="32" class="my-0">
                        <v-avatar :color="evento.cor + ' lighten-4'" size="32">
                          <v-icon :color="evento.cor" size="16">mdi-calendar</v-icon>
                        </v-avatar>
                      </v-list-item-avatar>

                      <v-list-item-content class="py-2">
                        <v-list-item-title class="text-body-2 font-weight-medium mb-1">
                          {{ evento.descricao }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="d-flex align-center">
                          <span class="text-caption">{{ formatarDia(evento.data) }}</span>
                          <v-chip
                            v-if="evento.is_sabado_letivo === true || evento.is_sabado_letivo === 1"
                            x-small
                            color="green"
                            class="ml-2"
                            dark
                            label
                          >
                            <v-icon x-small left>mdi-check</v-icon>
                            Letivo
                          </v-chip>
                          <v-chip v-else x-small color="red" class="ml-2" dark label>
                            <v-icon x-small left>mdi-close</v-icon>
                            Não letivo
                          </v-chip>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-divider
                      v-if="index < eventosFiltradosPorMes.length - 1"
                      :key="`divider-${index}`"
                      class="mx-3"
                    ></v-divider>
                  </template>
                </v-list>

                <div v-else class="empty-state pa-6 text-center">
                  <v-icon size="48" color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
                  <p class="text-body-2 grey--text text--darken-1 mt-3 mb-0">
                    Nenhum evento neste mês
                  </p>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-divider></v-divider>

      <v-card-actions class="dialog-actions pa-4">
        <v-spacer></v-spacer>
        <v-btn text color="grey darken-1" @click="modal = false" class="px-4">
          <v-icon left small>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="salvarData(data), $refs.dialog.save(data)"
          class="px-6"
          :disabled="!data"
          elevation="0"
        >
          <v-icon left small>mdi-check</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "EDatePickerCalendarioLetivo",
  props: {
    // Data selecionada (v-model)
    value: {
      type: String,
      default: null,
    },

    // Gestão de aula completa (para obter relacoesDiasHorarios)
    gestaoDeAula: {
      type: Object,
      required: true,
    },

    // Período da etapa (min/max do calendário)
    inicioPeriodoEtapa: {
      type: String, // formato: YYYY-MM-DD
      default: null,
    },

    fimPeriodoEtapa: {
      type: String, // formato: YYYY-MM-DD
      default: null,
    },

    // Validação externa (vee-validate)
    errorMessages: {
      type: Array,
      default: () => [],
    },

    // Desabilitar o componente
    disabled: {
      type: Boolean,
      default: false,
    },

    // Label do campo
    label: {
      type: String,
      default: "Escolha uma data",
    },

    // Modo de edição (para manter horário selecionado)
    editing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      modal: false,
      data: null, // Data interna no formato YYYY-MM-DD
      dataFormatada: null, // Data formatada DD/MM/YYYY
      mesVisivel: null,
      todosEventos: [],
      feriados: [],
      sabadosLetivos: [],
      diasPermitidosParaCriarAula: [0, 1, 2, 3, 4, 5, 6],
      todosHorariosDisponiveis: [],
      horariosDoComponente: [],
      filtrosDiaLetivo: {
        letivo: true, // Exibir dias letivos (padrão: ativo)
        naoLetivo: true, // Exibir dias não letivos (padrão: ativo)
      },
    };
  },

  computed: {
    eventosFiltradosPorMes() {
      if (!this.mesVisivel) return [];

      return (this.todosEventos || [])
        .filter((evento) => evento.data.startsWith(this.mesVisivel))
        .filter((evento) => {
          // Aplicar filtro de tipo de dia
          const isLetivo = evento.is_sabado_letivo === true || evento.is_sabado_letivo === 1;

          if (isLetivo && !this.filtrosDiaLetivo.letivo) {
            return false; // Filtro de letivo está desativado
          }

          if (!isLetivo && !this.filtrosDiaLetivo.naoLetivo) {
            return false; // Filtro de não letivo está desativado
          }

          return true; // Passa pelos filtros ativos
        })
        .sort((a, b) => {
          return parseInt(a.data.split("-")[2], 10) - parseInt(b.data.split("-")[2], 10);
        });
    },
  },

  async mounted() {
    await this.carregarFeriados();

    // Extrair dias permitidos da gestão
    if (this.gestaoDeAula?.relacoesDiasHorarios) {
      this.diasPermitidosParaCriarAula = this.gestaoDeAula.relacoesDiasHorarios.map(
        (g) => g.dia.id
      );
    }
  },

  watch: {
    // Quando a prop value mudar externamente
    value: {
      immediate: true,
      handler(val) {
        if (val && val !== this.dataFormatada) {
          this.dataFormatada = val;
          // Converter DD/MM/YYYY para YYYY-MM-DD
          const parts = val.split("/");
          if (parts.length === 3) {
            this.data = `${parts[2]}-${parts[1]}-${parts[0]}`;
          }
        }
      },
    },
  },

  methods: {
    async carregarFeriados() {
      try {
        const response = await this.$services.calendarioLetivoService.syncAll();
        this.todosEventos = response.map((evento) => ({
          data: evento.start,
          descricao: evento.descricao,
          cor: evento.color,
          is_feriado: evento.evento.is_feriado,
          is_sabado_letivo: evento.evento.is_sabado_letivo,
        }));
        this.feriados = this.todosEventos.filter((evento) => evento.is_feriado === true);
        this.sabadosLetivos = this.todosEventos.filter(
          (evento) => evento.is_sabado_letivo === true
        );
      } catch (error) {
        this.$handleError(error);
      }
    },

    allowedDates(val) {
      let dataFormatada;
      if (typeof val === "string") {
        dataFormatada = val.length > 10 ? val.slice(0, 10) : val;
      } else {
        dataFormatada = new Date(val).toISOString().split("T")[0];
      }

      const evento = (this.todosEventos || []).find((ev) => {
        const evData = ev.data.length > 10 ? ev.data.slice(0, 10) : ev.data;
        return evData === dataFormatada;
      });

      if (evento) {
        if (evento.is_sabado_letivo === true || evento.is_sabado_letivo === 1) {
          return true;
        }
        return false;
      }

      const ehFeriado = this.feriados.some((feriado) => {
        const fData = feriado.data.length > 10 ? feriado.data.slice(0, 10) : feriado.data;
        return fData === dataFormatada;
      });
      if (ehFeriado) return true;

      const diaDaSemana = new Date(dataFormatada).getDay();
      if (!this.diasPermitidosParaCriarAula.includes(diaDaSemana)) return false;

      return true;
    },

    async filtrarHorariosPorDiaDaSemana(diaDaSemana, dataSelecionada = this.data) {
      const toYMD = (v) => {
        if (!v) return "";
        if (typeof v === "string") return v.length > 10 ? v.slice(0, 10) : v;
        const d = v instanceof Date ? v : new Date(v);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`;
      };

      const dataFormatada = toYMD(dataSelecionada);
      const evento = (this.todosEventos || []).find((ev) => toYMD(ev.data) === dataFormatada);

      if (evento) {
        if (evento.is_sabado_letivo === true || evento.is_sabado_letivo === 1) {
          if (!this.todosHorariosDisponiveis || this.todosHorariosDisponiveis.length === 0) {
            await this.buscarTodosHorarios();
          }

          this.horariosDoComponente = this.todosHorariosDisponiveis || [];
          this.$emit("horarios-changed", this.horariosDoComponente);
          return;
        }

        if (evento.is_letivo === true || evento.is_letivo === 1) {
          const horariosDoDia = this.gestaoDeAula.relacoesDiasHorarios
            .filter((r) => r.dia.id === diaDaSemana)
            .map((r) => r.horario);
          this.horariosDoCompownente = horariosDoDia;
          this.$emit("horarios-changed", this.horariosDoComponente);
          return;
        }

        if (!this.editing) {
          this.horariosDoComponente = [];
          this.$emit("horarios-changed", []);
        }
        return;
      }

      if (!this.gestaoDeAula?.relacoesDiasHorarios) {
        this.horariosDoComponente = [];
        this.$emit("horarios-changed", []);
        return;
      }

      const horariosDoDia = this.gestaoDeAula.relacoesDiasHorarios
        .filter((relacao) => relacao.dia.id === diaDaSemana)
        .map((relacao) => relacao.horario);

      this.horariosDoComponente = horariosDoDia;
      this.$emit("horarios-changed", this.horariosDoComponente);
    },

    async buscarTodosHorarios() {
      try {
        const response = await this.$services.gestoesDeAulasService.getAllHorarios();
        this.todosHorariosDisponiveis = response.data || response;
        return this.todosHorariosDisponiveis;
      } catch (error) {
        this.$handleError(error);
        return [];
      }
    },

    toggleFiltro(tipo) {
      if (tipo === "letivo") {
        this.filtrosDiaLetivo.letivo = !this.filtrosDiaLetivo.letivo;
      } else if (tipo === "naoLetivo") {
        this.filtrosDiaLetivo.naoLetivo = !this.filtrosDiaLetivo.naoLetivo;
      }
    },

    atualizarMesVisivel(pickerDate) {
      const [ano, mes] = pickerDate.split("-");
      this.mesVisivel = `${ano}-${mes}`;
    },

    formatarDia(data) {
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}`;
    },

    getBadgeColor(date) {
      const evento = (this.todosEventos || []).find((ev) => {
        const evData = ev.data.length > 10 ? ev.data.slice(0, 10) : ev.data;
        return evData === date;
      });
      if (evento) {
        return (
          evento.cor ||
          (evento.is_sabado_letivo === true || evento.is_sabado_letivo === 1 ? "green" : "red")
        );
      }
      return "";
    },

    getBadgeContent(date) {
      const evento = (this.todosEventos || []).find((ev) => {
        const evData = ev.data.length > 10 ? ev.data.slice(0, 10) : ev.data;
        return evData === date;
      });
      if (evento) {
        if (evento.is_sabado_letivo === true || evento.is_sabado_letivo === 1) {
          return "Letivo";
        }
        return "Dia não letivo";
      }
      return "";
    },

    hasEvent(date) {
      return (this.todosEventos || []).some((ev) => {
        const evData = ev.data.length > 10 ? ev.data.slice(0, 10) : ev.data;
        return evData === date;
      });
    },

    isToday(date) {
      const today = new Date().toISOString().split("T")[0];
      return date === today;
    },

    getDayClasses(date) {
      const classes = [];
      if (this.isToday(date)) {
        classes.push("today-day");
      }
      return classes.join(" ");
    },

    salvarData(dataValue) {
      if (!dataValue) return;

      const date = new Date(dataValue);
      const dataFormatada = new Intl.DateTimeFormat("pt-BR", {
        timeZone: "UTC",
      }).format(date);

      this.dataFormatada = dataFormatada;

      this.$emit("input", dataFormatada);

      const diaDaSemana = new Date(dataValue).getDay();
      this.$emit("date-changed", {
        data: dataFormatada,
        dataOriginal: dataValue,
        diaDaSemana,
      });

      this.filtrarHorariosPorDiaDaSemana(diaDaSemana, dataValue);
    },
    limpar() {
      this.data = null;
      this.dataFormatada = null;
      this.horariosDoComponente = [];
      this.modal = false;
      this.$emit("input", null);
      this.$emit("date-changed", null);
      this.$emit("horarios-changed", []);
    },
  },
};
</script>

<style scoped>
/* Campo de input */
.date-input {
  transition: all 0.3s ease;
}

.date-input:hover:not(.v-input--is-disabled) >>> .v-input__control {
  background-color: rgba(0, 0, 0, 0.02);
}

.clear-btn {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.clear-btn:hover {
  opacity: 1;
}

/* Dialog */
.date-picker-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

/* Container do calendário */
.calendar-container {
  max-height: 75vh;
  overflow-y: auto;
}

.calendar-container::-webkit-scrollbar {
  width: 8px;
}

.calendar-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.calendar-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.calendar-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Card do calendário */
.calendar-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.calendar-wrapper {
  padding: 8px;
}

/* Dias customizados */
.today-day {
  font-weight: 700 !important;
  color: #1976d2 !important;
  border-radius: 50%;
  background-color: rgba(25, 118, 210, 0.15) !important;
  width: 32px !important;
  height: 32px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 4px !important;
}

/* Legenda */
.legend-card {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  background-color: #fafafa !important;
}

.legend-title {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.7);
}

.legend-chips {
  gap: 4px;
}

/* Card de eventos */
.events-card {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.events-header {
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.events-list-container {
  max-height: 450px;
  overflow-y: auto;
  flex: 1;
}

.events-list-container::-webkit-scrollbar {
  width: 6px;
}

.events-list-container::-webkit-scrollbar-track {
  background: transparent;
}

.events-list-container::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.events-list-container::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}

.event-item {
  transition: background-color 0.2s ease;
  border-radius: 8px;
  margin: 4px 8px;
}

.event-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.cursor-pointer {
  cursor: pointer;
}

/* Estado vazio */
.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Ações do dialog */
.dialog-actions {
  background-color: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

/* Responsividade */
@media (max-width: 960px) {
  .calendar-container {
    max-height: 85vh;
  }

  .events-list-container {
    max-height: 300px;
  }
}

/* Animações suaves */
.v-chip {
  transition: all 0.2s ease;
}

.v-btn {
  transition: all 0.2s ease;
}

/* Date picker customization */
::v-deep .v-date-picker-table {
  height: auto !important;
}

::v-deep .v-date-picker-table th,
::v-deep .v-date-picker-table td {
  height: 42px !important;
}

::v-deep .v-date-picker-header {
  padding: 8px 16px !important;
}

::v-deep .v-btn--icon.v-size--default {
  height: 36px !important;
  width: 36px !important;
}

/* Estilo dos badges de eventos */
::v-deep .v-badge__wrapper {
  width: 100%;
  height: 100%;
}

::v-deep .v-badge__badge {
  height: 6px !important;
  min-width: 6px !important;
  padding: 0 !important;
}
</style>
